import { Link } from 'react-router';
import type { CategoryTileViewModel } from '@rocco/ui/tile-category/view-models/category';

interface CategoryGroupProps {
    category: CategoryTileViewModel;
}

const CategoryGroup = ({ category }: CategoryGroupProps) => (
    <div className="flex flex-col">
        <Link
            to={category.link}
            className="text-4 font-semibold uppercase leading-[1.3] text-[#1F1F21] transition-colors hover:text-[#4B4B4B]"
        >
            {category.title}
        </Link>

        {category.subCategories.length > 0 && (
            <ul className="mt-3 flex flex-col gap-[5px]">
                {category.subCategories.map((sub) => (
                    <li key={`${category.id}-${sub.id}`}>
                        <Link
                            to={sub.link}
                            className="text-3.5 font-normal leading-[112.5%] tracking-[-0.28px] text-[#4B4B4B] transition-colors hover:text-[#1F1F21] hover:underline"
                        >
                            {sub.title}
                        </Link>
                    </li>
                ))}
            </ul>
        )}

        {/* "View all" sits a clear row below the sub-link rhythm — mt-6 (1.5rem ≈ 24px)
            matches the production sitemap spacing. */}
        <Link
            to={category.link}
            className="mt-6 text-3.5 font-normal text-[#4B4B4B] transition-colors hover:text-[#1F1F21] hover:underline"
            aria-label={`View all ${category.title} (${(category.itemsCount ?? 0).toLocaleString()})`}
        >
            View all ({(category.itemsCount ?? 0).toLocaleString()})
        </Link>
    </div>
);

interface ExploreAllCategoriesProps {
    categories: CategoryTileViewModel[];
    title?: string;
}

/**
 * Sitemap-style section displaying all categories in a multi-column grid.
 * Each category group shows its title, subcategory links, and a "View all (count)" link.
 *
 * Used across products and projects pages.
 */
export const ExploreAllCategories = ({
    categories,
    title = 'Explore all product categories',
}: ExploreAllCategoriesProps) => {
    if (categories.length === 0) {
        return null;
    }

    return (
        <section className="mb-10 md:mb-35">
            <div className="flex flex-col gap-9 px-6 md:px-12">
                <p className="text-3.5 font-medium leading-[1.22] tracking-[-0.75px] text-[#999]">
                    More to discover
                </p>
                <h2 className="text-[4rem] font-medium leading-[0.9] tracking-[-1.6px] text-[#141414] lg:text-[5rem]">
                    {title}
                </h2>
            </div>

            <hr className="mx-6 mt-8 border-t border-[#e5e5e5] md:mx-12" />

            {/* items-start: avoid row stretch so short columns don't sit in a tall cell (uneven gap above View all vs neighbours). */}
            <div className="mt-11 grid grid-cols-2 items-start gap-x-6 gap-y-12 px-6 md:grid-cols-5 md:gap-x-8 md:gap-y-16 md:px-12 lg:grid-cols-6">
                {categories.map((category) => (
                    <CategoryGroup key={category.id} category={category} />
                ))}
            </div>
        </section>
    );
};
